import * as Phaser from 'phaser';
import { ManifestPlacementPanel } from '../editor/ManifestPlacementPanel';
import { BUILD_TOOLS_SCENE_KEY } from '../scenes/sceneKeys';

interface AreaDef {
  id: string;
  label: string;
  color: string;
  prefixes: string[];
}

const AREAS: AreaDef[] = [
  {
    id: 'hill',
    label: 'The Hill',
    color: '#8bc34a',
    prefixes: ['CottageCrest', 'TheCrossing', 'TalkingStump', 'FarPillar', 'CottageInterior', 'TheMeadow', 'TheSlope', 'TheLowWall'],
  },
  {
    id: 'verdana',
    label: 'Verdana Fields',
    color: '#66bb6a',
    prefixes: ['Donchitos', 'SaplingField', 'WeepingStandingStone', 'OldGroveShrine', 'SageRoad', 'HamletInterior', 'RiversideWander', 'EmptyField', 'RidgeAboveDonchitos', 'FallenLog'],
  },
  {
    id: 'sablewind',
    label: 'Sablewind Reaches',
    color: '#ffb74d',
    prefixes: ['Caravanserai', 'GrownOverOasis', 'KiteHillShrine', 'WindChimeHollow', 'CaravanRoad', 'DuneCrestWander', 'DryCanyon', 'SandFlat', 'AbandonedLoom', 'OpenDesert'],
  },
  {
    id: 'ember',
    label: 'Ember Mire',
    color: '#ef5350',
    prefixes: ['Ashvein', 'JuggernautsHearth', 'EmberShrine', 'CoolingFlow', 'AshPass', 'SeedlingClearing', 'AshveinForgeInterior', 'LavaTube', 'CoolingCrust', 'HollowLogBridge', 'CharGarden'],
  },
  {
    id: 'frostmere',
    label: 'Frostmere Expanse',
    color: '#81d4fa',
    prefixes: ['FrostBridge', 'HearthmereHold', 'WakeNotYetCairn', 'FrozenFalls', 'NwPeaks', 'ThawLine', 'HearthHallInterior', 'IceCaveWander', 'SnowField', 'FrozenLake', 'Treeline'],
  },
  {
    id: 'shared',
    label: 'Shared / Neutral',
    color: '#ce93d8',
    prefixes: ['Hearth', 'PathHome', 'ForestHollow', 'LakesideStones', 'HilltopVista'],
  },
  {
    id: 'system',
    label: 'System / UI',
    color: '#90a4ae',
    prefixes: [],
  },
];

const TAB_HEIGHT = 32;
const SIDEBAR_WIDTH = 200;
const BG_COLOR = 0x0f0f14;
const BG_ALPHA = 0.92;
const TEXT_COLOR = '#e2e8f0';
const ACTIVE_ACCENT = '#4ade80';
const TAB_FONT = 'monospace';
const HEADER_FONT = "'Almendra', serif";
const SECTION_ROW_H = 22;
const SECTION_HEADER_H = 28;
const SECTION_PAD = 8;

const SKIP_SCENES = new Set([
  BUILD_TOOLS_SCENE_KEY,
  'BootScene',
  'PreloaderScene',
  'UIScene',
  'AmbientOverlayScene',
]);

function classifyScene(key: string): string {
  for (const area of AREAS) {
    if (area.prefixes.some((p) => key.startsWith(p))) {
      return area.id;
    }
  }
  return 'system';
}

interface TabData {
  key: string;
  container: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
  closeBtn: Phaser.GameObjects.Text;
  underline: Phaser.GameObjects.Rectangle;
}

export class BuildToolsScene extends Phaser.Scene {
  public static readonly KEY = BUILD_TOOLS_SCENE_KEY;

  private tabs: TabData[] = [];
  private activeTabKey: string | null = null;
  private tabBarBg!: Phaser.GameObjects.Rectangle;
  private tabContainer!: Phaser.GameObjects.Container;
  private tabScrollX = 0;

  private sidebarBg!: Phaser.GameObjects.Rectangle;
  private sidebarContainer!: Phaser.GameObjects.Container;
  private sidebarScrollContainer!: Phaser.GameObjects.Container;
  private sidebarVisible = true;
  private sidebarScrollY = 0;
  private sidebarMaxScroll = 0;
  private collapsedSections = new Set<string>();
  private sidebarMask!: Phaser.Display.Masks.GeometryMask;
  private sidebarMaskGraphics!: Phaser.GameObjects.Graphics;
  private toggleBtn!: Phaser.GameObjects.Text;
  private placementsBtn!: Phaser.GameObjects.Text;
  private placementPanel: ManifestPlacementPanel | null = null;

  constructor() {
    super({ key: BUILD_TOOLS_SCENE_KEY });
  }

  preload(): void {
    if (!this.cache.json.has('journey-manifest')) {
      this.load.json('journey-manifest', 'assets/scenes/journey-manifest.json');
    }
  }

  create(): void {
    this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');

    const w = this.scale.width;

    this.createTabBar(w);
    this.createSidebar();
    this.createToggleButton();
    this.createPlacementsButton();
    this.createPlacementPanel();
    this.setupInput();

    this.openTab('CottageCrestScene');
  }

  // ── Tab Bar ───────────────────────────────────────────────────────────

  private createTabBar(w: number): void {
    this.tabBarBg = this.add.rectangle(w / 2, TAB_HEIGHT / 2, w, TAB_HEIGHT, BG_COLOR, BG_ALPHA)
      .setScrollFactor(0)
      .setDepth(100);

    this.tabContainer = this.add.container(0, 0).setDepth(101);
  }

  private openTab(sceneKey: string): void {
    const existing = this.tabs.find((t) => t.key === sceneKey);
    if (existing) {
      this.switchToTab(sceneKey);
      return;
    }

    const tabW = 140;
    const tabH = TAB_HEIGHT - 4;
    const x = this.tabs.length * (tabW + 2);

    const container = this.add.container(x, 2);

    const bg = this.add.rectangle(tabW / 2, tabH / 2, tabW, tabH, 0x1e1e2e, 1)
      .setInteractive({ useHandCursor: true });

    const displayName = sceneKey.replace(/Scene$/, '');
    const label = this.add.text(8, tabH / 2, displayName, {
      fontFamily: TAB_FONT,
      fontSize: '11px',
      color: TEXT_COLOR,
    }).setOrigin(0, 0.5);

    const closeBtn = this.add.text(tabW - 8, tabH / 2, 'x', {
      fontFamily: TAB_FONT,
      fontSize: '11px',
      color: '#888',
    }).setOrigin(1, 0.5).setInteractive({ useHandCursor: true });

    const underline = this.add.rectangle(tabW / 2, tabH, tabW - 4, 2, Phaser.Display.Color.HexStringToColor(ACTIVE_ACCENT).color, 0);

    container.add([bg, label, closeBtn, underline]);
    this.tabContainer.add(container);

    const tab: TabData = { key: sceneKey, container, bg, label, closeBtn, underline };
    this.tabs.push(tab);

    bg.on('pointerdown', () => this.switchToTab(sceneKey));
    closeBtn.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      this.closeTab(sceneKey);
    });

    this.switchToTab(sceneKey);
  }

  private switchToTab(sceneKey: string): void {
    if (this.activeTabKey && this.activeTabKey !== sceneKey) {
      if (this.scene.isActive(this.activeTabKey) || this.scene.isPaused(this.activeTabKey)) {
        this.scene.stop(this.activeTabKey);
      }
    }

    for (const tab of this.tabs) {
      const isActive = tab.key === sceneKey;
      tab.bg.setFillStyle(isActive ? 0x2a2a3e : 0x1e1e2e);
      tab.underline.setAlpha(isActive ? 1 : 0);
      tab.label.setColor(isActive ? ACTIVE_ACCENT : TEXT_COLOR);
    }

    this.activeTabKey = sceneKey;

    if (!this.scene.isActive(sceneKey)) {
      this.scene.launch(sceneKey);
    }
    this.scene.bringToTop(BUILD_TOOLS_SCENE_KEY);
  }

  private closeTab(sceneKey: string): void {
    const idx = this.tabs.findIndex((t) => t.key === sceneKey);
    if (idx === -1) return;

    if (this.scene.isActive(sceneKey) || this.scene.isPaused(sceneKey)) {
      this.scene.stop(sceneKey);
    }

    const tab = this.tabs[idx];
    tab.container.destroy();
    this.tabs.splice(idx, 1);

    this.repositionTabs();

    if (this.activeTabKey === sceneKey) {
      this.activeTabKey = null;
      if (this.tabs.length > 0) {
        const nextIdx = Math.min(idx, this.tabs.length - 1);
        this.switchToTab(this.tabs[nextIdx].key);
      }
    }
  }

  private repositionTabs(): void {
    const tabW = 140;
    for (let i = 0; i < this.tabs.length; i++) {
      this.tabs[i].container.x = i * (tabW + 2) - this.tabScrollX;
    }
  }

  // ── Sidebar ───────────────────────────────────────────────────────────

  private createSidebar(): void {
    const h = this.scale.height;

    this.sidebarBg = this.add.rectangle(
      SIDEBAR_WIDTH / 2, TAB_HEIGHT + (h - TAB_HEIGHT) / 2,
      SIDEBAR_WIDTH, h - TAB_HEIGHT,
      BG_COLOR, BG_ALPHA,
    ).setScrollFactor(0).setDepth(100).setInteractive();

    this.sidebarContainer = this.add.container(0, 0).setDepth(101);
    this.sidebarScrollContainer = this.add.container(0, TAB_HEIGHT + SECTION_PAD);
    this.sidebarContainer.add(this.sidebarScrollContainer);

    this.sidebarMaskGraphics = this.make.graphics({});
    this.sidebarMaskGraphics.fillRect(0, TAB_HEIGHT, SIDEBAR_WIDTH, h - TAB_HEIGHT);
    this.sidebarMask = this.sidebarMaskGraphics.createGeometryMask();
    this.sidebarScrollContainer.setMask(this.sidebarMask);

    this.buildSidebarContent();
  }

  private buildSidebarContent(): void {
    this.sidebarScrollContainer.removeAll(true);

    const allScenes = this.game.scene.scenes
      .map((s) => s.sys.settings.key)
      .filter((key): key is string => typeof key === 'string' && !SKIP_SCENES.has(key));

    const grouped = new Map<string, string[]>();
    for (const area of AREAS) {
      grouped.set(area.id, []);
    }
    for (const key of allScenes) {
      const areaId = classifyScene(key);
      grouped.get(areaId)!.push(key);
    }

    let curY = 0;

    for (const area of AREAS) {
      const scenes = grouped.get(area.id)!;
      if (scenes.length === 0) continue;

      const collapsed = this.collapsedSections.has(area.id);
      const arrow = collapsed ? '▸' : '▾';

      const header = this.add.text(SECTION_PAD, curY, `${arrow} ${area.label}`, {
        fontFamily: HEADER_FONT,
        fontSize: '12px',
        color: area.color,
        fontStyle: 'bold',
      }).setOrigin(0, 0).setInteractive({ useHandCursor: true });

      header.on('pointerdown', () => {
        if (this.collapsedSections.has(area.id)) {
          this.collapsedSections.delete(area.id);
        } else {
          this.collapsedSections.add(area.id);
        }
        this.buildSidebarContent();
      });

      this.sidebarScrollContainer.add(header);
      curY += SECTION_HEADER_H;

      if (!collapsed) {
        for (const sceneKey of scenes) {
          const displayName = sceneKey.replace(/Scene$/, '');
          const sceneLabel = this.add.text(SECTION_PAD + 10, curY, displayName, {
            fontFamily: TAB_FONT,
            fontSize: '10px',
            color: TEXT_COLOR,
          }).setOrigin(0, 0).setInteractive({ useHandCursor: true });

          sceneLabel.on('pointerover', () => sceneLabel.setColor('#ffffff'));
          sceneLabel.on('pointerout', () => sceneLabel.setColor(TEXT_COLOR));
          sceneLabel.on('pointerdown', () => this.openTab(sceneKey));

          this.sidebarScrollContainer.add(sceneLabel);
          curY += SECTION_ROW_H;
        }
      }

      curY += 4;
    }

    const visibleH = this.scale.height - TAB_HEIGHT - SECTION_PAD * 2;
    this.sidebarMaxScroll = Math.max(0, curY - visibleH);
    this.sidebarScrollY = Phaser.Math.Clamp(this.sidebarScrollY, 0, this.sidebarMaxScroll);
    this.sidebarScrollContainer.y = TAB_HEIGHT + SECTION_PAD - this.sidebarScrollY;
  }

  private createToggleButton(): void {
    const y = TAB_HEIGHT + 6;
    this.toggleBtn = this.add.text(SIDEBAR_WIDTH + 4, y, '◀', {
      fontFamily: TAB_FONT,
      fontSize: '12px',
      color: TEXT_COLOR,
      backgroundColor: '#0f0f14',
      padding: { x: 3, y: 2 },
    }).setDepth(102).setInteractive({ useHandCursor: true });

    this.toggleBtn.on('pointerdown', () => this.toggleSidebar());
  }

  private createPlacementsButton(): void {
    const y = TAB_HEIGHT + 30;
    this.placementsBtn = this.add.text(SIDEBAR_WIDTH + 4, y, 'S4', {
      fontFamily: TAB_FONT,
      fontSize: '11px',
      color: TEXT_COLOR,
      backgroundColor: '#1e293b',
      padding: { x: 5, y: 3 },
    }).setDepth(102).setInteractive({ useHandCursor: true });

    this.placementsBtn.on('pointerdown', () => this.placementPanel?.toggle());
  }

  private createPlacementPanel(): void {
    const raw = this.cache.json.get('journey-manifest') as unknown;
    if (!raw) return;
    try {
      this.placementPanel = new ManifestPlacementPanel(raw);
      this.placementPanel.attach(document.body);
    } catch (err) {
      console.warn('[BuildToolsScene] Failed to initialise S4 placement panel:', err);
    }
  }

  private toggleSidebar(): void {
    this.sidebarVisible = !this.sidebarVisible;
    this.sidebarBg.setVisible(this.sidebarVisible);
    this.sidebarContainer.setVisible(this.sidebarVisible);
    this.toggleBtn.setText(this.sidebarVisible ? '◀' : '▶');
    this.toggleBtn.x = this.sidebarVisible ? SIDEBAR_WIDTH + 4 : 4;
    this.placementsBtn.x = this.sidebarVisible ? SIDEBAR_WIDTH + 4 : 4;
  }

  // ── Input ─────────────────────────────────────────────────────────────

  private setupInput(): void {
    const tabKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.TAB);
    tabKey?.on('down', (event: KeyboardEvent) => {
      event.preventDefault();
      this.toggleSidebar();
    });

    const placementsKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.P);
    placementsKey?.on('down', (event: KeyboardEvent) => {
      if (!event.shiftKey) return;
      event.preventDefault();
      this.placementPanel?.toggle();
    });

    this.input.on('wheel', (pointer: Phaser.Input.Pointer, _gx: unknown[], _gy: unknown, _gz: unknown, dy: number) => {
      if (this.sidebarVisible && pointer.x < SIDEBAR_WIDTH) {
        this.sidebarScrollY = Phaser.Math.Clamp(
          this.sidebarScrollY + dy * 0.5,
          0,
          this.sidebarMaxScroll,
        );
        this.sidebarScrollContainer.y = TAB_HEIGHT + SECTION_PAD - this.sidebarScrollY;
      } else if (pointer.y < TAB_HEIGHT) {
        this.tabScrollX = Math.max(0, this.tabScrollX + dy * 0.5);
        this.repositionTabs();
      }
    });
  }

  shutdown(): void {
    this.placementPanel?.destroy();
    this.placementPanel = null;
  }
}
